import { TableHeader } from '@common/types/common';
import { Table } from '@components/display/tables';
import { PublicLayoutContent } from '@components/layout/public';
import { Search } from '@components/utility/inputs/Search';
import { useGetInstallationsQuery } from '@extendedWarranty/services';
import { IInstallation, InstallationCertificateState } from '@extendedWarranty/types';
import { useLazyDownload } from '@hooks/useDownload';
import { usePagination } from '@hooks/usePagination';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import AddIcon from '@mui/icons-material/Add';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, Button, CircularProgress, Link, TableCell, Typography, TypographyProps } from '@mui/material';
import { handleError } from '@utils/common';
import { ReactElement, useCallback, useState } from 'react';

const PAGE_SIZE_OPTIONS = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
];

const labelProps: TypographyProps = {
    className: 'text-[10px] sm:text-sm',
    fontWeight: {
        xs: 500,
        sm: 700,
    },
};

const TABLE_HEADERS: TableHeader<Partial<IInstallation> & { downloads: string }>[] = [
    {
        id: 'installation_name',
        label: 'Installation name',
        labelProps,
    },
    {
        id: 'date_installed',
        label: 'Installation Date',
        labelProps,
    },
    {
        id: 'downloads',
        label: 'Download',
        disabled: true,
        labelProps,
    },
];

interface Props {
    hideButtons?: boolean;
}

const InstallationOverview = ({ hideButtons }: Props) => {
    const [downloadCertificate, { isLoading: isCertificateLoading }] = useLazyDownload();
    const [certificateDownloadUrls, setCertificateDownloadUrls] = useState<
        Record<number, Partial<InstallationCertificateState>>
    >({});

    const { filters, handlePageChange, sort, handleSortClick, handlePageSizeChange, search, setSearch } = usePagination<
        IInstallation & { downloads: string }
    >({
        allowedColumns: ['installation_name', 'date_installed'],
        defaultSort: 'installation_name',
    });

    const { data, isFetching } = useGetInstallationsQuery({
        page_size: Number(filters.page_size) || 10,
        filters: { search: [filters.search] },
        page: Number(filters.page) || 1,
        ordering: filters.sort,
    });

    const navigate = usePrefixedNavigate();

    const handleCertificateDownload = useCallback(
        async (id: number) => {
            if (certificateDownloadUrls[id]) return;
            // Prevent duplicate requests
            const installationCertificateData: Partial<InstallationCertificateState> = { isLoading: true };
            setCertificateDownloadUrls((prev) => ({ ...prev, [id]: installationCertificateData }));
            try {
                const result = await downloadCertificate({ relativePath: `extended-warranty/request/${id}/certificate` });
                if (result) {
                    installationCertificateData.isLoading = isCertificateLoading;
                    installationCertificateData.url = URL.createObjectURL(result as Blob);
                    setCertificateDownloadUrls((prev) => ({ ...prev, [id]: installationCertificateData }));
                }
            } catch (err) {
                handleError('Error while downloading the certificate.', err);
            }
        },
        [downloadCertificate, setCertificateDownloadUrls, certificateDownloadUrls, isCertificateLoading]
    );

    const renderRow = useCallback(
        (installation: Partial<IInstallation>, collapsibleAction?: ReactElement) => (
            <>
                <TableCell>
                    <div className="flex items-center gap-2">
                        {collapsibleAction}
                        <Typography
                            fontSize={{
                                xs: 10,
                                sm: 14,
                            }}
                            className="truncate"
                        >
                            {installation.installation_name}
                        </Typography>
                    </div>
                </TableCell>
                <TableCell>
                    <Typography
                        fontSize={{
                            xs: 10,
                            sm: 14,
                        }}
                    >
                        {installation.date_installed}
                    </Typography>
                </TableCell>
                <TableCell>
                    <div className="flex gap-2 items-center">
                        {installation &&
                        certificateDownloadUrls[installation.id as number] &&
                        certificateDownloadUrls[installation.id as number].isLoading ? (
                            <CircularProgress className="sm:mr-[2px]" size={22} color="neutral-900" />
                        ) : (
                            <SaveAltIcon />
                        )}
                        <Link
                            underline="none"
                            className="cursor-pointer"
                            onClick={() =>
                                (installation && installation.id && handleCertificateDownload(installation.id)) ??
                                undefined
                            }
                            href={
                                (installation?.id &&
                                    certificateDownloadUrls[installation.id] &&
                                    certificateDownloadUrls[installation?.id].url) ||
                                undefined
                            }
                            download={
                                installation?.id && certificateDownloadUrls[installation?.id]
                                    ? `${installation.installation_name}_certificate.pdf`
                                    : undefined
                            }
                            fontSize={{
                                xs: 10,
                                sm: 14,
                            }}
                        >
                            {installation &&
                            certificateDownloadUrls[installation.id as number] &&
                            certificateDownloadUrls[installation.id as number].isLoading
                                ? 'Downloading...'
                                : 'Download certificates'}
                        </Link>
                    </div>
                </TableCell>
            </>
        ),
        [handleCertificateDownload, certificateDownloadUrls]
    );

    const collapsableContent = (item: Partial<IInstallation>) => (
        <div className="py-5 sm:py-10 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="max-w-[300px]">
                <Typography fontStyle={'italic'} variant="body2" fontSize={14}>
                    {item.serial_numbers?.split(',').join(', ')}
                </Typography>
            </div>
            {!hideButtons ? (
                <div className="sm:ml-auto">
                    <Button
                        color="secondary"
                        className="rounded"
                        disableElevation={false}
                        startIcon={<AddIcon />}
                        size="large"
                        onClick={() =>
                            navigate('/extended-warranty/upload-serial-numbers', {
                                state: item,
                            })
                        }
                    >
                        <Typography
                            fontFamily='"Roboto","Helvetica","Arial",sans-serif'
                            fontWeight={500}
                            fontSize={15}
                            className="leading-[unset]"
                        >
                            ADD SERIAL NUMBER(S) TO SYSTEM
                        </Typography>
                    </Button>
                </div>
            ) : null}
        </div>
    );

    return (
        <PublicLayoutContent>
            <Box className="mx-3 md:mx-14 mt-14 md:mt-32" sx={{ color: (theme) => theme.palette['neutral-900'].main }}>
                <div className="flex flex-col gap-5 md:flex-row md:items-end mb-10">
                    <div>
                        <Typography variant="h5" fontWeight={700} marginBottom={1}>
                            Extended warranty
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            Registered Installations
                        </Typography>
                    </div>
                    <div className="md:ml-auto flex flex-col md:flex-row gap-3">
                        {!hideButtons ? (
                            <Button
                                className="!rounded h-[40px]"
                                disableElevation={false}
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/extended-warranty/upload-serial-numbers')}
                            >
                                <Typography
                                    fontFamily='"Roboto","Helvetica","Arial",sans-serif'
                                    fontSize={15}
                                    fontWeight={500}
                                    className="leading-[unset]"
                                >
                                    Add New System
                                </Typography>
                            </Button>
                        ) : null}
                        <div className="w-full md:w-[240px]">
                            <Search
                                placeholder="Search"
                                fullWidth
                                onChange={(e) => setSearch(e.target.value)}
                                value={search}
                            />
                        </div>
                    </div>
                </div>
                <div className="bg-white">
                    <Table
                        headers={TABLE_HEADERS}
                        sort={sort}
                        onSortClick={handleSortClick}
                        data={data?.results ?? []}
                        renderRow={renderRow}
                        emptyListTxt={'No installations available'}
                        page={Number(filters.page)}
                        onPageChange={handlePageChange}
                        count={data?.count ?? 0}
                        bottomContainerProps={{
                            className: 'py-4',
                        }}
                        collapsableContent={collapsableContent}
                        rowsPerPage={Number(filters.page_size) ?? 10}
                        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        setRowsPerPage={handlePageSizeChange}
                        isLoading={isFetching}
                    />
                </div>
            </Box>
        </PublicLayoutContent>
    );
};

export { InstallationOverview };
